import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/useBusiness";

export interface WhitelabelSettings {
  id: string;
  business_id: string;
  brand_name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  favicon_url: string | null;
  custom_domain: string | null;
  email_from_name: string | null;
  email_from_address: string | null;
  hide_powered_by: boolean;
  custom_css: string | null;
  is_active: boolean;
  access_blocked: boolean;
}

interface WhitelabelContextType {
  whitelabel: WhitelabelSettings | null;
  isWhitelabel: boolean;
  isLoading: boolean;
  brandName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  hidePoweredBy: boolean;
}

const WhitelabelContext = createContext<WhitelabelContextType>({
  whitelabel: null,
  isWhitelabel: false,
  isLoading: true,
  brandName: "Workia",
  logoUrl: null,
  primaryColor: "",
  secondaryColor: "",
  hidePoweredBy: false,
});

// Convert HEX to HSL
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Apply whitelabel theme to CSS variables
function applyWhitelabelTheme(settings: WhitelabelSettings) {
  const root = document.documentElement;
  
  if (settings.primary_color) {
    const primaryHSL = hexToHSL(settings.primary_color);
    root.style.setProperty('--primary', primaryHSL);
    root.style.setProperty('--brand-primary', primaryHSL);
  }
  
  if (settings.secondary_color) {
    const secondaryHSL = hexToHSL(settings.secondary_color);
    root.style.setProperty('--secondary', secondaryHSL);
    root.style.setProperty('--brand-secondary', secondaryHSL);
  }
  
  // Update favicon if provided
  if (settings.favicon_url) {
    const existingFavicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (existingFavicon) {
      existingFavicon.href = settings.favicon_url;
    } else {
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = settings.favicon_url;
      document.head.appendChild(favicon);
    }
  }
  
  // Update page title with brand name
  if (settings.brand_name) {
    const currentTitle = document.title;
    if (currentTitle.includes('Workia')) {
      document.title = currentTitle.replace('Workia', settings.brand_name);
    }
  }
  
  // Apply custom CSS if provided
  if (settings.custom_css) {
    let styleElement = document.getElementById('whitelabel-custom-css');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'whitelabel-custom-css';
      document.head.appendChild(styleElement);
    }
    styleElement.textContent = settings.custom_css;
  }
}

// Clear whitelabel theme
function clearWhitelabelTheme() {
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--brand-primary');
  root.style.removeProperty('--brand-secondary');
  
  const customStyle = document.getElementById('whitelabel-custom-css');
  if (customStyle) {
    customStyle.remove();
  }
}

export const WhitelabelProvider = ({ children }: { children: ReactNode }) => {
  const [whitelabel, setWhitelabel] = useState<WhitelabelSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { data: business } = useBusiness();

  useEffect(() => {
    const fetchWhitelabel = async () => {
      setIsLoading(true);
      
      try {
        // First, try to detect by custom domain
        const currentDomain = window.location.hostname;
        
        // Skip detection for localhost and main domains
        const skipDomains = ['localhost', '127.0.0.1', 'workia.com.br', 'www.workia.com.br', 'lovableproject.com'];
        const shouldSkipDomainCheck = skipDomains.some(d => currentDomain.includes(d));
        
        if (!shouldSkipDomainCheck) {
          const { data: domainData, error: domainError } = await supabase
            .rpc('get_whitelabel_by_domain', { p_domain: currentDomain });
          
          if (!domainError && domainData && domainData.length > 0) {
            const settings = domainData[0] as WhitelabelSettings;
            setWhitelabel(settings);
            applyWhitelabelTheme(settings);
            setIsLoading(false);
            return;
          }
        }
        
        // If no domain match and user is logged in, check by business_id
        // Only apply whitelabel if is_active is true
        if (business?.id) {
          const { data: businessData, error: businessError } = await supabase
            .rpc('get_whitelabel_by_business', { p_business_id: business.id });
          
          if (!businessError && businessData && businessData.length > 0) {
            const settings = businessData[0] as WhitelabelSettings;
            // Only apply if whitelabel is explicitly active
            if (settings.is_active && !settings.access_blocked) {
              setWhitelabel(settings);
              applyWhitelabelTheme(settings);
              setIsLoading(false);
              return;
            }
          }
        }
        
        // No whitelabel found, clear any existing theme
        clearWhitelabelTheme();
        setWhitelabel(null);
      } catch (error) {
        console.error('Error fetching whitelabel settings:', error);
        clearWhitelabelTheme();
        setWhitelabel(null);
      }
      
      setIsLoading(false);
    };

    fetchWhitelabel();
  }, [business?.id]);

  const value: WhitelabelContextType = {
    whitelabel,
    isWhitelabel: !!whitelabel,
    isLoading,
    brandName: whitelabel?.brand_name || "Workia",
    logoUrl: whitelabel?.logo_url || null,
    primaryColor: whitelabel?.primary_color || "",
    secondaryColor: whitelabel?.secondary_color || "",
    hidePoweredBy: whitelabel?.hide_powered_by || false,
  };

  return (
    <WhitelabelContext.Provider value={value}>
      {children}
    </WhitelabelContext.Provider>
  );
};

export const useWhitelabel = () => {
  const context = useContext(WhitelabelContext);
  if (!context) {
    throw new Error("useWhitelabel must be used within a WhitelabelProvider");
  }
  return context;
};
